import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import {
  getCompanyEmploymentProfile,
  type EmploymentAssignmentDetail,
  type EmploymentProfile,
} from '@/api/services/employment.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { CompanyEmploymentHistory } from './CompanyEmploymentHistory';
import { EmployeeTimeline } from './EmployeeTimeline';
import { EmployeeLifecycleActions } from './EmployeeLifecycleActions';
import { ProbationProfileCard } from '@/features/hr/components/probation/ProbationProfileCard';
import { CaseProfileCard } from '@/features/hr/components/cases/CaseProfileCard';
import { PolicyProfileCard } from '@/features/hr/components/policies/PolicyProfileCard';
import { isActionable } from '@/features/hr/components/probation/probationFormat';
import { exitTypeLabel } from '@/lib/exitTypes';

function formatInstant(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

function formatStatus(value: string | null | undefined): string {
  return value ? value.replaceAll('_', ' ') : '—';
}

const LIFECYCLE_TONES: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  active: 'success',
  probation: 'warning',
  onboarding: 'info',
  preboarding: 'info',
  on_leave: 'warning',
  suspended: 'danger',
  notice: 'warning',
  exited: 'neutral',
  archived: 'neutral',
};

function Field({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): React.ReactElement {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{value || '—'}</dd>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{description}</p>
      )}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function assignmentFields(assignment: EmploymentAssignmentDetail): React.ReactElement {
  return (
    <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
      <Field label="Job title" value={assignment.jobTitleName} />
      <Field label="Department" value={assignment.departmentName} />
      <Field label="Team" value={assignment.teamName} />
      <Field label="Position" value={assignment.positionName} />
      <Field label="Grade" value={assignment.gradeName} />
      <Field label="Employment type" value={assignment.employmentTypeName} />
      <Field label="Reporting manager" value={assignment.reportingManagerName} />
      <Field label="Work location" value={assignment.workLocation} />
    </dl>
  );
}

function OverviewTab({
  profile,
  companyId,
}: {
  profile: EmploymentProfile;
  companyId: string;
}): React.ReactElement {
  const { currentAssignment, scheduledAssignments, reportingLine, otherEmployments } = profile;
  return (
    <div className="space-y-4">
      <Section
        title="Lifecycle"
        description={`Current status: ${formatStatus(profile.employment.lifecycleStatus)}`}
      >
        <EmployeeLifecycleActions
          companyId={companyId}
          userId={profile.employee._id}
          lifecycleStatus={profile.employment.lifecycleStatus}
          probationCaseOpen={profile.probation ? isActionable(profile.probation.status) : false}
        />
      </Section>
      <ProbationProfileCard probation={profile.probation} />
      <CaseProfileCard cases={profile.hrCases} />
      <PolicyProfileCard companyId={companyId} userId={profile.employee._id} />
      <Section title={`Assignment at ${formatInstant(profile.asOf)}`}>
        {currentAssignment ? (
          assignmentFields(currentAssignment)
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No assignment was effective at this time.
          </p>
        )}
      </Section>

      <Section title="Next shift">
        {profile.nextShift ? (
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Date" value={formatDate(profile.nextShift.date)} />
            <Field label="Shift" value={profile.nextShift.name} />
            <Field
              label="Time"
              value={
                profile.nextShift.startTime && profile.nextShift.endTime
                  ? `${profile.nextShift.startTime} – ${profile.nextShift.endTime}`
                  : null
              }
            />
            <Field label="Status" value={formatStatus(profile.nextShift.status)} />
          </dl>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">No upcoming shifts.</p>
        )}
      </Section>

      <Section
        title="Scheduled changes"
        description="Approved assignments that take effect later. These are not current."
      >
        {scheduledAssignments.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">No scheduled changes.</p>
        ) : (
          <ol className="divide-y divide-gray-200 dark:divide-gray-700">
            {scheduledAssignments.map((assignment) => (
              <li key={assignment._id} className="space-y-3 py-3 first:pt-0 last:pb-0">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  Effective {formatInstant(assignment.effectiveFrom)}
                </p>
                {assignmentFields(assignment)}
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section title="Reporting line">
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <Field label="Reports to" value={reportingLine.manager?.name} />
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Direct reports
            </dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {reportingLine.directReports.length === 0 ? (
                '—'
              ) : (
                <ul>
                  {reportingLine.directReports.map((report) => (
                    <li key={report.userId}>
                      {report.name}
                      <span className="text-gray-500 dark:text-gray-400">
                        {' '}
                        · {report.employeeId}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        </dl>
      </Section>

      <Section
        title="Other company employments"
        description="This employee's employments in other companies of the organization."
      >
        {otherEmployments.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No employment in other companies.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {otherEmployments.map((item) => (
              <li
                key={item._id}
                className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0"
              >
                <span className="text-sm text-gray-900 dark:text-gray-100">
                  {item.companyName ?? 'Company'}
                </span>
                <span className="text-xs capitalize text-gray-600 dark:text-gray-300">
                  {formatStatus(item.lifecycleStatus)} · joined {formatDate(item.joinedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function PersonalTab({ profile }: { profile: EmploymentProfile }): React.ReactElement {
  const { employee } = profile;
  const address = employee.contactInfo?.address;
  const addressLine =
    [address?.street, address?.city, address?.state, address?.country, address?.postalCode]
      .filter(Boolean)
      .join(', ') || null;
  const emergency = employee.personalInfo?.emergencyContact;
  return (
    <div className="space-y-4">
      <Section title="Identity">
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
          <Field label="Employee ID" value={employee.employeeId} />
          <Field label="Work email" value={employee.email} />
          <Field label="Account status" value={formatStatus(employee.accountStatus)} />
          <Field
            label="Date of birth"
            value={
              profile.canViewSensitive === false
                ? 'Hidden'
                : formatDate(employee.personalInfo?.dateOfBirth)
            }
          />
          <Field label="Gender" value={employee.personalInfo?.gender} />
          <Field label="Nationality" value={employee.personalInfo?.nationality} />
          <Field label="Marital status" value={employee.personalInfo?.maritalStatus} />
          <Field label="Last sign-in" value={formatInstant(employee.lastLoginAt)} />
        </dl>
      </Section>
      <Section title="Contact">
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
          <Field label="Personal email" value={employee.contactInfo?.personalEmail} />
          <Field label="Phone" value={employee.contactInfo?.phone} />
          <Field label="Alternate phone" value={employee.contactInfo?.alternatePhone} />
          <Field label="Address" value={addressLine} />
        </dl>
      </Section>
      <Section title="Emergency contact">
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
          <Field label="Name" value={emergency?.name} />
          <Field label="Relationship" value={emergency?.relationship} />
          <Field label="Phone" value={emergency?.phone} />
          <Field label="Email" value={emergency?.email} />
        </dl>
      </Section>
    </div>
  );
}

function CompensationTab({ profile }: { profile: EmploymentProfile }): React.ReactElement {
  if (!profile.canViewCompensation || !profile.compensation) {
    return (
      <Section title="Compensation">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          You do not have permission to view compensation for this employee.
        </p>
      </Section>
    );
  }
  const { compensation } = profile;
  const amount =
    compensation.hasRate === false
      ? undefined
      : compensation.compensationType === 'hourly'
        ? compensation.hourlyRate
        : compensation.baseSalary;
  return (
    <Section
      title="Compensation"
      description={
        compensation.source === 'company_override'
          ? 'This company overrides the organization-level compensation.'
          : 'This company uses the organization-level compensation.'
      }
    >
      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
        <Field label="Type" value={formatStatus(compensation.compensationType)} />
        <Field
          label={compensation.compensationType === 'hourly' ? 'Hourly rate' : 'Base salary'}
          value={typeof amount === 'number' ? amount.toLocaleString() : 'No rate set'}
        />
        <Field label="Currency" value={compensation.currency} />
        <Field label="Pay frequency" value={formatStatus(compensation.payFrequency)} />
      </dl>
    </Section>
  );
}

export function CompanyEmployeeProfile({
  companyId,
  userId,
  onClose,
}: {
  companyId: string;
  userId: string;
  onClose: () => void;
}): React.ReactElement {
  const [asOfLocal, setAsOfLocal] = useState('');
  const [tab, setTab] = useState('overview');
  const parsedAsOf = asOfLocal ? new Date(asOfLocal) : null;
  const invalidAsOf = parsedAsOf !== null && Number.isNaN(parsedAsOf.getTime());
  const at = parsedAsOf && !invalidAsOf ? parsedAsOf.toISOString() : undefined;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hr-employee-profile', companyId, userId, at],
    queryFn: () => getCompanyEmploymentProfile(companyId, userId, at),
    enabled: !invalidAsOf,
  });
  const employeeName = data
    ? `${data.employee.firstName} ${data.employee.lastName}`.trim()
    : 'Employee profile';

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[94dvh] w-[calc(100vw-1rem)] max-w-5xl overflow-y-auto p-0">
        <div className="border-b border-gray-200 px-5 py-4 pr-14 dark:border-gray-700">
          <DialogTitle>{employeeName}</DialogTitle>
          <DialogDescription className="mt-1">
            {data?.employee.employeeId ? `${data.employee.employeeId} · ` : ''}Employee profile
            workspace
          </DialogDescription>
          {data && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge
                variant={LIFECYCLE_TONES[data.employment.lifecycleStatus] ?? 'neutral'}
                className="capitalize"
              >
                {formatStatus(data.employment.lifecycleStatus)}
              </Badge>
              <span className="text-xs text-gray-600 dark:text-gray-300">
                Joined {formatDate(data.employment.joinedAt)}
              </span>
              {data.employment.exitedAt && (
                <span className="text-xs text-gray-600 dark:text-gray-300">
                  {data.employment.lastWorkingDate
                    ? `Last working day ${formatDate(data.employment.lastWorkingDate)}`
                    : `Exited ${formatDate(data.employment.exitedAt)}`}
                  {' · '}Exit type: {exitTypeLabel(data.employment.exitType)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="space-y-5 p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full max-w-xs">
              <Input
                id="profile-as-of"
                label="View as of (local time)"
                type="datetime-local"
                value={asOfLocal}
                onChange={(event) => setAsOfLocal(event.target.value)}
              />
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={() => setAsOfLocal('')}>
              Now
            </Button>
          </div>
          {invalidAsOf && (
            <p role="alert" className="text-sm text-danger">
              Enter a valid date and time.
            </p>
          )}
          {isLoading ? (
            <div className="flex justify-center py-10" role="status" aria-live="polite">
              <Spinner />
              <span className="sr-only">Loading employee profile</span>
            </div>
          ) : error ? (
            <div className="space-y-3" role="alert">
              <p className="text-sm text-danger">{getErrorMessage(error)}</p>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : data ? (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="personal">Personal</TabsTrigger>
                <TabsTrigger value="compensation">Compensation</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-4">
                <OverviewTab profile={data} companyId={companyId} />
              </TabsContent>
              <TabsContent value="personal" className="mt-4">
                <PersonalTab profile={data} />
              </TabsContent>
              <TabsContent value="compensation" className="mt-4">
                <CompensationTab profile={data} />
              </TabsContent>
              <TabsContent value="timeline" className="mt-4">
                <EmployeeTimeline companyId={companyId} userId={userId} />
              </TabsContent>
              <TabsContent value="history" className="mt-4">
                <CompanyEmploymentHistory companyId={companyId} userId={userId} at={at} />
              </TabsContent>
            </Tabs>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
